import { Button, Field } from './ui';
import './TermDates.css';

/** One term, as the API stores it (backend services/termCalendar.ts). */
export type TermDate = { name: string; startsOn: string; endsOn: string };

const pad = (n: number) => String(n).padStart(2, '0');
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
/** The academic year a date belongs to (from August). */
const academicYear = (ymd: string) => {
  const [y, m] = ymd.split('-').map(Number);
  return m >= 8 ? y : y - 1;
};
const yearLabel = (y: number) => `${y}/${String(y + 1).slice(2)}`;

/** Typical English term dates for an academic year, as a starting point to correct. */
export function typicalTerms(year: number): TermDate[] {
  return [
    { name: 'Autumn', startsOn: `${year}-09-02`, endsOn: `${year}-12-18` },
    { name: 'Spring', startsOn: `${year + 1}-01-05`, endsOn: `${year + 1}-03-26` },
    { name: 'Summer', startsOn: `${year + 1}-04-12`, endsOn: `${year + 1}-07-21` },
  ];
}

/** What's wrong with the terms as entered, or null when they can be saved. */
export function termDatesProblem(terms: TermDate[]): string | null {
  if (!terms.length) return 'Add your term dates.';
  for (const t of terms) {
    if (!t.name.trim()) return 'Give each term a name.';
    if (!t.startsOn || !t.endsOn) return `Add the start and end of ${t.name.trim() || 'each term'}.`;
    if (t.endsOn <= t.startsOn) return `${t.name.trim()} has to end after it starts.`;
  }
  const sorted = [...terms].sort((a, b) => a.startsOn.localeCompare(b.startsOn));
  for (let i = 1; i < sorted.length; i += 1) {
    if (sorted[i].startsOn <= sorted[i - 1].endsOn) return `${sorted[i - 1].name.trim()} and ${sorted[i].name.trim()} overlap.`;
  }
  return null;
}

/**
 * The organisation's terms: a row each (name, first day, last day), grouped by
 * academic year. Starts empty, with one tap to fill in typical dates to
 * correct; settings can add next year's.
 */
export function TermDatesField({ value, onChange }: { value: TermDate[]; onChange: (terms: TermDate[]) => void }) {
  const thisYear = academicYear(today());
  const years = [...new Set(value.map((t) => (t.startsOn ? academicYear(t.startsOn) : thisYear)))].sort();
  const lastYear = years.length ? Math.max(...years) : thisYear - 1;
  const update = (i: number, patch: Partial<TermDate>) => onChange(value.map((t, j) => (j === i ? { ...t, ...patch } : t)));
  const remove = (i: number) => onChange(value.filter((_, j) => j !== i));
  const rows = value.map((t, i) => ({ t, i, year: t.startsOn ? academicYear(t.startsOn) : thisYear }));

  return (
    <div className="terms">
      {value.length === 0 ? (
        <div className="terms-empty">
          <p className="muted">No term dates yet. Start from typical dates and change them to match your calendar.</p>
          <Button type="button" variant="secondary" onClick={() => onChange(typicalTerms(thisYear))}>
            Fill in typical dates for {yearLabel(thisYear)}
          </Button>
        </div>
      ) : (
        years.map((y) => (
          <fieldset key={y} className="terms-year">
            <legend>{yearLabel(y)}</legend>
            {rows
              .filter((r) => r.year === y)
              .map(({ t, i }) => (
                <div key={i} className="terms-row">
                  <Field
                    id={`term-${i}-name`}
                    label="Term"
                    value={t.name}
                    maxLength={40}
                    onChange={(e) => update(i, { name: e.target.value })}
                  />
                  <Field id={`term-${i}-start`} label="First day" type="date" value={t.startsOn} onChange={(e) => update(i, { startsOn: e.target.value })} />
                  <Field id={`term-${i}-end`} label="Last day" type="date" value={t.endsOn} onChange={(e) => update(i, { endsOn: e.target.value })} />
                  <button type="button" className="terms-remove" onClick={() => remove(i)} aria-label={`Remove ${t.name || 'this term'}`} title="Remove">
                    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                      <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </button>
                </div>
              ))}
          </fieldset>
        ))
      )}
      {value.length ? (
        <div className="terms-actions">
          <Button type="button" variant="ghost" onClick={() => onChange([...value, { name: '', startsOn: '', endsOn: '' }])} disabled={value.length >= 12}>
            Add a term
          </Button>
          {lastYear <= thisYear ? (
            <Button type="button" variant="ghost" onClick={() => onChange([...value, ...typicalTerms(lastYear + 1)])} disabled={value.length > 9}>
              Add {yearLabel(lastYear + 1)}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
